import {ApolloQueryResult} from 'apollo-client';

import {apolloClientNoCache} from '../../../../apolloClient';
import {
  BackendLinesDeltaQueryDocument,
  BackendLinesDeltaQueryInternalIdDocument,
  BackendLinesDeltaQueryInternalIdQuery,
  BackendLinesDeltaQueryInternalIdQueryVariables,
  BackendLinesDeltaQueryQuery,
  BackendLinesDeltaQueryQueryVariables,
  BucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
  BucketedRunsDeltaQueryWithoutRunInfoDocument,
  BucketedRunsDeltaQueryWithoutRunInfoQuery,
  BucketedRunsDeltaQueryWithoutRunInfoQueryVariables,
  GroupedBackendLinesDeltaQueryDocument,
  GroupedBackendLinesDeltaQueryQuery,
  GroupedBackendLinesDeltaQueryQueryVariables,
  GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoDocument,
  GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
  GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables,
  GroupedBucketedRunsDeltaQueryWithoutRunInfoDocument,
  GroupedBucketedRunsDeltaQueryWithoutRunInfoQuery,
  GroupedBucketedRunsDeltaQueryWithoutRunInfoQueryVariables,
} from '../../../../generated/graphql';
import {propagateErrorsContext} from '../../../../util/errors';
import {QueryVars, queryVars} from './queryVars';
import {
  BackendLinesQueryPromise,
  BucketedQueryPromise,
  BucketedQueryResult,
  GroupedBackendLinesQueryPromise,
} from './types';

export const TIMING_DELAY = 5; // create a small delay before dispatching the second function to try to randomize execution order affecting the results;

export async function doShadowBucketedApiQuery(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string,
  backendLinesFirst: boolean = Math.random() < 0.5
): BackendLinesQueryPromise {
  const uniq = window.crypto?.randomUUID(); // I want a unique property so I can associate two identical queries in datadog and see how they compare
  const variables = {
    ...vars,
    traceId: uniq,
  };

  // Helper function to delay execution
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  let backendLinesPromise: BackendLinesQueryPromise;
  let bucketedPromise: Promise<BucketedQueryResult | void>;

  if (backendLinesFirst) {
    // Start backend query first, wait 3ms, then start bucketed query
    backendLinesPromise = doBackendLinesApiQuery(
      variables,
      currentRuns,
      lastUpdated
    );
    await delay(TIMING_DELAY);
    bucketedPromise = doBucketedApiQuery(
      variables,
      currentRuns,
      lastUpdated
    ).catch(err => {
      console.error(
        'Error fetching bucketed data in shadowed backend lines query:',
        err
      );
    });
  } else {
    // Start bucketed query first, wait 3ms, then start backend query
    bucketedPromise = doBucketedApiQuery(
      variables,
      currentRuns,
      lastUpdated
    ).catch(err => {
      console.error(
        'Error fetching bucketed data in shadowed backend lines query:',
        err
      );
    });
    await delay(TIMING_DELAY);
    backendLinesPromise = doBackendLinesApiQuery(
      variables,
      currentRuns,
      lastUpdated
    );
  }

  return backendLinesPromise;
}

export function doBackendLinesApiQuery(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string
): BackendLinesQueryPromise {
  return apolloClientNoCache.query<
    BackendLinesDeltaQueryQuery,
    BackendLinesDeltaQueryQueryVariables
  >({
    context: propagateErrorsContext(),
    query: BackendLinesDeltaQueryDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function doGroupedBackendLinesApiQuery(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string
): GroupedBackendLinesQueryPromise {
  return apolloClientNoCache.query<
    GroupedBackendLinesDeltaQueryQuery,
    GroupedBackendLinesDeltaQueryQueryVariables
  >({
    context: propagateErrorsContext(),
    query: GroupedBackendLinesDeltaQueryDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function doBackendLinesApiQueryByInternalId(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string
): BackendLinesQueryPromise {
  return apolloClientNoCache.query<
    BackendLinesDeltaQueryInternalIdQuery,
    BackendLinesDeltaQueryInternalIdQueryVariables
  >({
    context: propagateErrorsContext(),
    query: BackendLinesDeltaQueryInternalIdDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function doBucketedApiQuery(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string
): BucketedQueryPromise {
  return apolloClientNoCache.query<
    BucketedRunsDeltaQueryWithoutRunInfoQuery,
    BucketedRunsDeltaQueryWithoutRunInfoQueryVariables
  >({
    context: propagateErrorsContext(),
    query: BucketedRunsDeltaQueryWithoutRunInfoDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function doBucketedApiQueryByInternalId(
  vars: QueryVars,
  currentRuns: string[],
  lastUpdated: string
): BucketedQueryPromise {
  return apolloClientNoCache.query<
    BucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
    BucketedRunsDeltaQueryWithoutRunInfoQueryVariables
  >({
    context: propagateErrorsContext(),
    query: BucketedRunsDeltaQueryWithoutRunInfoDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export type GroupedBucketedQueryPromise = Promise<
  ApolloQueryResult<GroupedBucketedRunsDeltaQueryWithoutRunInfoQuery>
>;

export function doGroupedBucketedApiQuery(
  vars: QueryVars,
  currentRuns: string[],
  lastUpdated: string
): GroupedBucketedQueryPromise {
  return apolloClientNoCache.query<
    GroupedBucketedRunsDeltaQueryWithoutRunInfoQuery,
    GroupedBucketedRunsDeltaQueryWithoutRunInfoQueryVariables
  >({
    context: propagateErrorsContext(),
    query: GroupedBucketedRunsDeltaQueryWithoutRunInfoDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

export function doGroupedBucketedApiQueryByInternalId(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string
): GroupedBucketedQueryPromise {
  return apolloClientNoCache.query<
    GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
    GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables
  >({
    context: propagateErrorsContext(),
    query: GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}
